import React, { useState } from 'react';
import { ArrowRight, SkipForward } from 'lucide-react';
import { Button } from '../../common/Button';
import { Logo } from '../../common/Logo';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import apiClient from '../../../services/apiClient';
import { useToast } from '../../common/Toast';
import { useNavigate } from 'react-router-dom';

interface WelcomeFormProps {
  userName?: string;
  email?: string;
  onComplete: (formData: WelcomeFormData) => Promise<void>;
  onSkip: () => void;
}

export interface WelcomeFormData {
  goal: string;
  referralSource: string;
}

// Predefined options for the goal and referral source fields
const GOAL_OPTIONS = [
  "Practice for upcoming interviews",
  "Improve interview skills generally",
  "Prepare for specific company interviews",
  "Learn new technical concepts",
  "Get feedback on my interviewing technique",
  "Other"
];

const REFERRAL_OPTIONS = [
  "JobProt Platform",
  "Search Engine",
  "Social Media",
  "Friend or Colleague",
  "Online Forum",
  "Professional Network",
  "Other"
];

export function WelcomeForm({ userName, email, onComplete, onSkip }: WelcomeFormProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<WelcomeFormData>({
    goal: '',
    referralSource: ''
  });
  
  const { isMobile } = useResponsive();
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  // Transform form data to match API requirements
  const transformFormData = (data: WelcomeFormData, skipped = false) => {
    return {
      purpose: skipped ? 'SKIPPED' : data.goal,
      source: skipped ? 'SKIPPED' : data.referralSource
    };
  };
  
  // Submit survey data to the API
  const submitSurvey = async (data: WelcomeFormData, skipped = false) => {
    try {
      setLoading(true);
      
      const apiData = transformFormData(data, skipped);
      console.log('Submitting survey data:', apiData);
      
      const response = await apiClient.post('/users/survey', apiData);
      
      console.log('Survey submission response:', response);
      
      // Show success toast
      showToast(
        skipped ? 'Preferences skipped' : 'Preferences saved successfully', 
        'success'
      );
      
      // Call the onComplete callback to update parent component state
      if (!skipped) {
        await onComplete(data);
      } else {
        // If skipped, still need to call onSkip for parent component
        onSkip();
      }
      
      // Clear all welcome form flags since we're done with it
      localStorage.removeItem('showing_welcome_form');
      sessionStorage.removeItem('welcome_form_event_dispatched');
      
      // Update user status in API to mark them as no longer a new user
      try {
        await apiClient.post('/users/update-status', { newUser: false });
        console.log('User status updated to not new');
      } catch (err) {
        console.error('Failed to update user status, but continuing with navigation', err);
      }
      
      // Add a small delay to ensure all state updates are completed
      // before navigating away from the page
      console.log('WelcomeForm: Waiting before navigation to ensure state updates are complete');
      
      setTimeout(() => {
        console.log('WelcomeForm: Navigating to setup-profile');
        // Navigate to setup-profile page with replace to prevent back navigation to welcome form
        navigate('/setup-profile', { replace: true });
      }, 500);
      
    } catch (error: any) {
      console.error('Failed to submit survey:', error);
      
      // Show error toast with appropriate message
      showToast(
        error.message || 'Failed to save preferences. Please try again.', 
        'error'
      );
      
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitSurvey(formData);
  };
  
  const handleSkip = async () => {
    await submitSurvey(formData, true);
  };
  
  const isValid = formData.goal.trim() !== '' && formData.referralSource.trim() !== '';
  
  return (
    <div className="relative h-full w-full">
      {/* Glass container without borders - scrollable on BOTH mobile AND desktop */}
      <div className={`relative h-full bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md overflow-y-auto ${isMobile ? 'max-h-[90vh]' : ''}`}>
      
        {/* Content with proper vertical centering */}
        <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} p-8 md:p-12 min-h-full`}>
          <div className={`${isMobile ? 'mb-6' : 'mb-10'} flex flex-col items-center`}>
            <Logo isCentered showText={true} />
            
            <h1 className="text-3xl font-bold mt-6 text-center text-text-primary-light dark:text-text-primary-dark">
              Hi, {userName?.split(' ')[0] || email?.split('@')[0]}!
            </h1>
            <p className="text-base font-medium mt-2 text-center text-text-secondary-light dark:text-text-secondary-dark">
              Please tell us a bit more about you.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-sm">
            {/* Goal Selection */}
            <div className="space-y-2">
              <label 
                htmlFor="goal" 
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark"
              >
                What is your purpose of using MockTrail?
              </label>
              <select
                id="goal"
                name="goal"
                value={formData.goal}
                onChange={handleChange}
                className="w-full p-3 text-base bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent transition-all"
                required
              >
                <option value="" disabled>Select your goal...</option>
                {GOAL_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            {/* Referral Source */}
            <div className="space-y-2">
              <label 
                htmlFor="referralSource" 
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark"
              >
                How did you find MockTrail?
              </label>
              <select
                id="referralSource"
                name="referralSource"
                value={formData.referralSource}
                onChange={handleChange}
                className="w-full p-3 text-base bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent transition-all"
                required
              >
                <option value="" disabled>Select option...</option>
                {REFERRAL_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 gap-4">
              {/* Skip Button (Secondary) */}
              <Button
                variant="outline"
                fullWidth
                type="button"
                onClick={handleSkip}
              >
                Skip
              </Button>
              
              {/* Continue Button (Primary) */}
              <Button
                variant="primary"
                fullWidth
                type="submit"
                disabled={!isValid}
                loading={loading}
                className="relative overflow-hidden group"
                rightIcon={<ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />}
              >
                Continue
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 